import React from 'react';

import type { TeamMember } from '../api/teams';

type ActionType = 'deactivate' | 'reactivate';

interface ConfirmationModalProps {
	isOpen: boolean;
	user: TeamMember | null;
	actionType: ActionType;
	onClose: () => void;
	onConfirm: () => void;
	isLoading?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
	isOpen,
	user,
	actionType,
	onClose,
	onConfirm,
	isLoading = false,
}) => {
	if (!isOpen || !user) return null;

	const isDeactivate = actionType === 'deactivate';
	const isReactivate = actionType === 'reactivate';

	// Configuration based on action type
	const config = {
		deactivate: {
			title: 'Deactivate User',
			icon: (
				<svg
					className="h-8 w-8 text-orange-600"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
					/>
				</svg>
			),
			iconBg: 'bg-orange-100',
			message: `Are you sure you want to deactivate ${user.name || user.email.split('@')[0]}?`,
			details: {
				bg: 'bg-orange-50',
				border: 'border-orange-200',
				text: 'text-orange-800',
				textSecondary: 'text-orange-700',
				textTertiary: 'text-orange-600',
				items: [
					'Remove their access to the system',
					'Preserve all their data and files',
					'Allow reactivation later if needed',
				],
				footer: 'This action can be undone by an administrator.',
			},
			button: {
				bg: 'bg-orange-600',
				hover: 'hover:bg-orange-700',
				text: isLoading ? 'Deactivating...' : 'Deactivate User',
			},
		},
		reactivate: {
			title: 'Reactivate User',
			icon: (
				<svg
					className="h-8 w-8 text-green-600"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M5 13l4 4L19 7"
					/>
				</svg>
			),
			iconBg: 'bg-green-100',
			message: `Are you sure you want to reactivate ${user.name || user.email.split('@')[0]}?`,
			details: {
				bg: 'bg-green-50',
				border: 'border-green-200',
				text: 'text-green-800',
				textSecondary: 'text-green-700',
				textTertiary: 'text-green-600',
				items: [
					'Restore their access to the system',
					'Allow them to log in again',
					'Maintain all their previous data and files',
				],
				footer: null,
			},
			button: {
				bg: 'bg-green-600',
				hover: 'hover:bg-green-700',
				text: 'Reactivate User',
			},
		},
	};

	const currentConfig = config[actionType];

	return (
		<div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999]">
			<div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
				<div className="mt-3">
					<div
						className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full ${currentConfig.iconBg} mb-4`}
					>
						{currentConfig.icon}
					</div>
					<h3 className="text-lg font-medium text-gray-900 mb-4 text-center">
						{currentConfig.title}
					</h3>
					<p className="text-sm text-gray-600 mb-4 text-center">
						{currentConfig.message}
					</p>
					<div
						className={`${currentConfig.details.bg} border ${currentConfig.details.border} rounded-md p-3 mb-4`}
					>
						<p className={`text-sm ${currentConfig.details.text}`}>
							<strong>This will:</strong>
						</p>
						<ul
							className={`text-sm ${currentConfig.details.textSecondary} mt-1 space-y-1`}
						>
							{currentConfig.details.items.map((item, index) => (
								<li key={index}>• {item}</li>
							))}
						</ul>
						{currentConfig.details.footer && (
							<p
								className={`text-xs ${currentConfig.details.textTertiary} mt-2`}
							>
								{currentConfig.details.footer}
							</p>
						)}
					</div>
					<div className="flex justify-end space-x-3 mt-6">
						<button
							onClick={onClose}
							className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
						>
							Cancel
						</button>
						<button
							onClick={onConfirm}
							disabled={isLoading}
							className={`px-4 py-2 text-white rounded-md transition-colors ${currentConfig.button.bg} ${currentConfig.button.hover} disabled:opacity-50 disabled:cursor-not-allowed`}
						>
							{currentConfig.button.text}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ConfirmationModal;
